'use client';

import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { Button, Flex, Select, Spacer, Text } from '@/_components';
import { rangeParser } from '@/_utils/parse';
import { convertToRanges } from '@/_utils/documents';
import styles from './PdfPage.module.css';
import { getClasses } from '@/_utils/classnames';
import type { PdfPageProps } from './PdfPage.types';

export const PdfPage: FC<PdfPageProps> = ({
  currentlySelectedDoc,
  documents,
  pageNumber,
  setCurrentlySelectedDoc,
  setDocuments,
  thumbnail,
  showDeletedPages,
  editingDocId,
}) => {
  const [editingDocIdState, setEditingDocIdState] = useState<number | null>(
    null,
  );
  const isRemoved: boolean = useMemo(() => {
    return (documents || []).some(
      (d: any) =>
        d.name === '__to_remove__' && (d.pages || []).includes(pageNumber),
    );
  }, [documents, pageNumber]);
  const imgClassNames: string = getClasses(styles.image, {
    [styles.selectedImage]:
      currentlySelectedDoc?.pages &&
      currentlySelectedDoc?.pages.includes(pageNumber),
    [styles.removedImage]: isRemoved && !!showDeletedPages,
  });

  const selectedDocRef = useRef<HTMLDivElement>(null);

  // Mirror editingDocId prop into local state for internal checks
  useEffect(() => {
    setEditingDocIdState(
      typeof editingDocId === 'number' ? editingDocId : null,
    );
  }, [editingDocId]);

  useEffect(() => {
    const firstPageOfRange = currentlySelectedDoc?.pages[0];

    if (firstPageOfRange === pageNumber && selectedDocRef.current) {
      selectedDocRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [currentlySelectedDoc, pageNumber]);

  const onThumbnailClick = (): void => {
    if (!currentlySelectedDoc) return;
    // Only allow add/remove in explicit edit mode for this document
    if (!(editingDocIdState && currentlySelectedDoc.id === editingDocIdState)) {
      return;
    }

    const newDocuments = documents.map((doc) => {
      // Skip documents that aren't currently selected
      if (doc.id !== currentlySelectedDoc.id) return doc;

      // If page is already selected -> remove it
      // If page isn't selected -> add it and sort numerically
      const updatedPages = doc.pages.includes(pageNumber)
        ? doc.pages.filter((page: number) => page !== pageNumber)
        : [...doc.pages, pageNumber].sort((a, b) => a - b);

      // Create a human-readable (e.g. 1-4) string representation of selected pages
      const pageRanges =
        updatedPages.length === 0
          ? ''
          : updatedPages.every(
                (page: number, i: number) =>
                  i === 0 || page === updatedPages[i - 1] + 1,
              )
            ? `${updatedPages[0]}-${updatedPages[updatedPages.length - 1]}`
            : updatedPages.join(', ');

      // Create updated document with new pages and range string
      const updatedDoc = {
        ...doc,
        pages: updatedPages,
        pageRanges,
      };

      // Update the currently selected document in parent state
      setCurrentlySelectedDoc(updatedDoc);
      return updatedDoc;
    });

    // Update all documents in parent state
    setDocuments(newDocuments);
  };

  const selectItems = useMemo(() => {
    const base = [{ name: 'Unclassified', value: '__unclassified__' }];
    const docItems = documents
      .filter((d: any) => d.name !== '__to_remove__')
      .map((d: any, idx: number) => {
        const fullLabel = d.differentiator
          ? `${d.name} (${d.differentiator})`
          : d.name;
        return { name: fullLabel as string, value: String(idx) };
      });
    return [...base, ...docItems];
  }, [documents]);

  const firstAssignedIndex = useMemo(() => {
    for (let i = 0; i < documents.length; i += 1) {
      const d = documents[i];
      if (d.name === '__to_remove__') continue;
      const pages = rangeParser(d.pageRanges || '');
      if (pages.includes(pageNumber)) return i;
    }
    return -1;
  }, [documents, pageNumber]);

  const selectedValue: string =
    firstAssignedIndex >= 0 ? String(firstAssignedIndex) : '__unclassified__';

  const handleAssign = (value: string) => {
    if (value === '__unclassified__') {
      // remove page from any document
      const updated = documents.map((d: any) => {
        const pages = Array.isArray(d.pages) ? d.pages : [];
        const filtered = pages.filter((p: number) => p !== pageNumber);
        if (filtered.length === pages.length) return d;
        return {
          ...d,
          pages: filtered,
          pageRanges: convertToRanges(filtered).join(', '),
        } as any;
      });
      setDocuments(updated);
      return;
    }

    const targetIndex = parseInt(value, 10);
    const updated = documents.map((d: any, idx: number) => {
      const pages = Array.isArray(d.pages) ? d.pages : [];
      if (idx === targetIndex) {
        const merged = Array.from(new Set([...pages, pageNumber])).sort(
          (a, b) => a - b,
        );
        return {
          ...d,
          pages: merged,
          pageRanges: convertToRanges(merged).join(', '),
        } as any;
      }
      // Remove from any other document, including __to_remove__ to auto-undelete
      if (pages.includes(pageNumber)) {
        const filtered = pages.filter((p: number) => p !== pageNumber);
        return {
          ...d,
          pages: filtered,
          pageRanges: convertToRanges(filtered).join(', '),
        } as any;
      }
      return d;
    });
    setDocuments(updated);
  };

  return (
    <div className={styles.pageContainer} key={pageNumber} ref={selectedDocRef}>
      <Flex isYCentered={true} gap={4}>
        <Text color="secondary" type="small">
          Page {pageNumber}
        </Text>
        <Spacer gap={4} />
        <Select
          label="Assign to document"
          isLabelHidden={true}
          items={selectItems}
          selectedValue={selectedValue}
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
            handleAssign(e.target.value)
          }
        />
        <Button
          iconRight={isRemoved ? 'arrow-counter-clockwise' : 'trash'}
          intent="danger-ghost"
          size="tiny"
          value={isRemoved ? 'Undo remove' : 'Remove page'}
          onClick={() => {
            const hasBucket = (documents || []).some(
              (d: any) => d.name === '__to_remove__',
            );

            if (!isRemoved) {
              // Mark as removed: ensure bucket exists and remove page from all other docs
              let next = documents.map((d: any) => {
                const pages = Array.isArray(d.pages) ? d.pages : [];
                if (d.name === '__to_remove__') {
                  const merged = Array.from(
                    new Set([...pages, pageNumber]),
                  ).sort((a, b) => a - b);
                  return {
                    ...d,
                    pages: merged,
                    pageRanges: convertToRanges(merged).join(', '),
                  } as any;
                }
                const filtered = pages.filter((p: number) => p !== pageNumber);
                return pages.length !== filtered.length
                  ? {
                      ...d,
                      pages: filtered,
                      pageRanges: convertToRanges(filtered).join(', '),
                    }
                  : d;
              });
              if (!hasBucket) {
                const bucket = {
                  id:
                    Math.max(
                      0,
                      ...documents.map((d: any) => Number(d.id) || 0),
                    ) + 1,
                  name: '__to_remove__',
                  detailed_name: '__to_remove__',
                  differentiator: '',
                  pages: [pageNumber],
                  pageRanges: String(pageNumber),
                } as any;
                next = [...next, bucket];
              }
              setDocuments(next);
              return;
            }

            // Undo removal: remove page from the bucket only
            const reverted = documents.map((d: any) => {
              if (d.name !== '__to_remove__') return d;
              const pgs = Array.isArray(d.pages) ? d.pages : [];
              const filtered = pgs.filter((p: number) => p !== pageNumber);
              return {
                ...d,
                pages: filtered,
                pageRanges: convertToRanges(filtered).join(', '),
              } as any;
            });
            setDocuments(reverted);
          }}
          isIconOnly={true}
          withTooltip={true}
          tooltip={isRemoved ? 'Undo remove' : 'Remove page'}
        />
      </Flex>

      <img
        alt={`Page ${pageNumber}`}
        className={imgClassNames}
        onClick={onThumbnailClick}
        src={thumbnail}
      />
    </div>
  );
};
